import { useMemo } from 'react';
import { useDispatch } from 'react-redux';
import { Dispatch } from 'redux';
import { PersonasController } from '../controllers/PersonasController';
import commonHandler from '../errorHandlers/common-handler';
import { StoreState } from '../store-interfaces';
import CrudActionContainer from './CrudActionContainer';

class PersonasCRUDActions extends CrudActionContainer {
  resetAction: string;

  constructor() {
    super('PERSONAS');
    this.resetAction = super.makeActionKey('RESET');
  }

  retrieveAll(remotePageId: string) {
    return async (dispatch: Dispatch<StoreState>) => {
      dispatch(this.busy(this.retrieveAction));

      try {
        const response = await PersonasController.retrieveAll(remotePageId);
        dispatch(this.retrievedAll(response));
        return response;
      } catch (e) {
        commonHandler(dispatch)(e);
      }
    };
  }

  retrieveMe(remotePageId: string) {
    return async (dispatch: Dispatch<StoreState>) => {
      dispatch(this.busy(this.retrieveAction));

      try {
        const response = await PersonasController.retrieveMe(remotePageId);
        dispatch(this.retrieved(response));
        return response;
      } catch (e) {
        commonHandler(dispatch)(e);
      }
    };
  }

  retrieve(remotePageId: string, personaId: number) {
    return async (dispatch: Dispatch<StoreState>) => {
      try {
        const response = await PersonasController.retrieve(
          remotePageId,
          personaId,
        );
        dispatch(this.retrieved(response));
        return response;
      } catch (e) {
        if (e.name === 'TypeError') {
          // Persona missing, we can ignore.
          return;
        }
        commonHandler(dispatch)(e);
      }
    };
  }

  updateMe(personaName: string, personaAssetId?: number) {
    return async (dispatch: Dispatch<StoreState>) => {
      dispatch(this.busy(this.retrieveAllAction));

      try {
        await PersonasController.updateMe(personaName, personaAssetId);
        dispatch(this.reset());
      } catch (e) {
        commonHandler(dispatch)(e);
      }
    };
  }

  reset() {
    return async (dispatch: Dispatch<StoreState>) => {
      dispatch({ type: this.resetAction });
    };
  }
}

export const personasCRUDActions = new PersonasCRUDActions();

export const usePersonasCRUDActions = () => {
  const dispatch: Dispatch<StoreState> = useDispatch();

  return useMemo(
    () => ({
      retrieveAll: (
        ...args: Parameters<typeof personasCRUDActions.retrieveAll>
      ) => dispatch(personasCRUDActions.retrieveAll(...args)),
      retrieveMe: (
        ...args: Parameters<typeof personasCRUDActions.retrieveMe>
      ) => dispatch(personasCRUDActions.retrieveMe(...args)),
      retrieve: (...args: Parameters<typeof personasCRUDActions.retrieve>) =>
        dispatch(personasCRUDActions.retrieve(...args)),
      updateMe: (...args: Parameters<typeof personasCRUDActions.updateMe>) =>
        dispatch(personasCRUDActions.updateMe(...args)),
      busy: () => dispatch(personasCRUDActions.busy()),
      unbusy: () => dispatch(personasCRUDActions.unbusy()),
    }),
    [dispatch],
  );
};
